import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction
} from 'mobx';

import { ConfigStore } from 'widget/stores/ConfigStore';
import { SubmissionStore } from 'widget/stores/SubmissionStore';
import {
  MessageSource,
  LayoutVersion,
  Placement,
  SessionState,
  InitializationState
} from 'widget/types/common';
import { WIDGET_ANIMATION_DURATION_MS, WIDGET_ID } from 'widget/consts/widget';
import { WidgetEvents, InjectEvents, TokenPayload } from 'widget/types/events';

import { ApiStore } from '../ApiStore';
import { MessagesStore } from '../MessagesStore';
import { ToastStore } from '../ToastStore';
import { WebSocketStore } from '../WebSocketStore';
type PrivateFields =
  | '_isOpen'
  | '_initToken'
  | '_initializationState'
  | '_sessionState'
  | '_layoutVersion'
  | '_placement';

export class RootStore {
  private _isOpen: boolean = false;
  private _initToken: string | null = null;
  private _initializationState: InitializationState =
    InitializationState.NOT_INITIALIZED;
  private _sessionState: SessionState = SessionState.IDLE;
  private _layoutVersion: LayoutVersion = LayoutVersion.DESKTOP;
  private _placement: Placement = Placement.RIGHT;
  private _pendingAuthTokenRequest: Promise<string | null> | null = null;
  private _closeAnimationTimerId: ReturnType<typeof setTimeout> | null = null;

  readonly apiStore: ApiStore;
  readonly messagesStore: MessagesStore;
  readonly webSocketStore: WebSocketStore;
  readonly configStore: ConfigStore;
  readonly submissionStore: SubmissionStore;
  readonly toastStore: ToastStore;

  constructor() {
    this.apiStore = new ApiStore();
    this.messagesStore = new MessagesStore(this);
    this.webSocketStore = new WebSocketStore(this);
    this.configStore = new ConfigStore(this);
    this.submissionStore = new SubmissionStore(this);
    this.toastStore = new ToastStore();

    makeObservable<RootStore, PrivateFields>(this, {
      _isOpen: observable,
      _initToken: observable,
      _initializationState: observable,
      _sessionState: observable,
      _layoutVersion: observable,
      _placement: observable,

      isOpen: computed,
      isInitialized: computed,
      isInitializing: computed,
      isAuthenticated: computed,
      sessionState: computed,
      isSessionOpened: computed,
      layoutVersion: computed,
      placement: computed,

      init: action,
      toggleOpen: action,
      open: action,
      confirmOpen: action,
      close: action,
      hideUntilReload: action,
      destroy: action,
      setInitToken: action,
      openSession: action,
      setLayoutVersion: action,
      setPlacement: action
    });
  }

  get isInitialized(): boolean {
    return this._initializationState === InitializationState.INITIALIZED;
  }

  get isInitializing(): boolean {
    return this._initializationState === InitializationState.LOADING;
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  get isAuthenticated(): boolean {
    return this._initToken !== null;
  }

  get initToken(): string | null {
    return this._initToken;
  }

  get sessionState(): SessionState {
    return this._sessionState;
  }

  get isSessionOpened(): boolean {
    return this._sessionState === SessionState.OPENED;
  }

  get layoutVersion(): LayoutVersion {
    return this._layoutVersion;
  }

  get placement(): Placement {
    return this._placement;
  }

  setLayoutVersion = (layout: LayoutVersion): void => {
    this._layoutVersion = layout;
  };

  setPlacement = (placement: Placement): void => {
    this._placement = placement;
  };

  setInitToken = (token: string): void => {
    this._initToken = token;

    // Отправляем подтверждение успешной верификации в OuterScript
    this._sendMessageToParent(WidgetEvents.VERIFICATION_SUCCESS);
  };

  sendVerificationError = (): void => {
    this._sendMessageToParent(WidgetEvents.VERIFICATION_ERROR);
  };

  init = async (): Promise<void> => {
    if (this.isInitializing) {
      return;
    }

    this._initializationState = InitializationState.LOADING;

    // Восстанавливаем session_token из localStorage (через OuterScript)
    // до любых API-запросов, чтобы все запросы шли с X-Auth-Token
    await this.requestAuthToken();

    await this.configStore.init();

    runInAction(() => {
      this.submissionStore.init(
        this.configStore.customizationConfig,
        this.configStore.submitStatuses
      );
      this._initializationState = InitializationState.INITIALIZED;
    });
  };

  openSession = async (): Promise<boolean> => {
    if (this._sessionState === SessionState.OPENED) {
      return true;
    }

    if (this._sessionState === SessionState.OPENING) {
      return false;
    }

    if (!this._initToken) {
      return false;
    }

    runInAction(() => {
      this._sessionState = SessionState.OPENING;
    });

    try {
      await this.requestAuthToken();

      const response = await this.apiStore.openSession(this._initToken);

      if (!response.isError) {
        this.apiStore.setToken(response.data.data.session_token);

        this._sendMessageToParent(WidgetEvents.SAVE_TOKEN, {
          token: response.data.data.session_token
        });

        const tasks = [
          this.webSocketStore.connect(this.messagesStore.handleWsMessage),
          this.messagesStore.loadHistory()
        ];

        await Promise.all(tasks);

        runInAction(() => {
          this._sessionState = SessionState.OPENED;
        });

        return true;
      }

      runInAction(() => {
        this._sessionState = SessionState.ERROR;
      });
      return false;
    } catch (error) {
      runInAction(() => {
        this._sessionState = SessionState.ERROR;
      });
      return false;
    }
  };

  /**
   * Запрос токена авторизации через postMessage.
   * Возвращает Promise, который резолвится когда токен будет получен.
   * Параллельные вызовы возвращают один и тот же Promise.
   */
  requestAuthToken = (): Promise<string | null> => {
    const existingToken = this.apiStore.getToken();
    if (existingToken) {
      return Promise.resolve(existingToken);
    }

    if (this._pendingAuthTokenRequest) {
      return this._pendingAuthTokenRequest;
    }

    if (!window.parent || window.parent === window) {
      return Promise.resolve(null);
    }

    this._pendingAuthTokenRequest = new Promise<string | null>((resolve) => {
      let isResolved = false;

      const cleanup = (): void => {
        window.removeEventListener('message', handleTokenMessage);
      };

      const handleTokenMessage = (event: MessageEvent): void => {
        if (
          event.data?.source !== MessageSource.INJECT ||
          event.data?.event !== InjectEvents.AUTH_TOKEN_RESPONSE
        ) {
          return;
        }

        if (isResolved) {
          return;
        }

        isResolved = true;
        cleanup();

        const token = event.data.payload?.token || null;
        if (token) {
          this.apiStore.setToken(token);
        }

        resolve(token);
      };

      window.addEventListener('message', handleTokenMessage);

      this._sendMessageToParent(WidgetEvents.REQUEST_AUTH_TOKEN);

      setTimeout(() => {
        if (!isResolved) {
          isResolved = true;
          cleanup();
          resolve(null);
        }
      }, 5000);
    }).finally(() => {
      this._pendingAuthTokenRequest = null;
    });

    return this._pendingAuthTokenRequest;
  };

  open = async (): Promise<void> => {
    if (this._closeAnimationTimerId !== null) {
      clearTimeout(this._closeAnimationTimerId);
      this._closeAnimationTimerId = null;
    }

    if (this._isOpen) {
      return;
    }

    if (this._sessionState !== SessionState.OPENED) {
      const success = await this.openSession();
      if (!success) {
        return;
      }
    }

    this._sendMessageToParent(WidgetEvents.OPEN_REQUEST);
  };

  /**
   * Подтверждение открытия виджета от OuterScript
   * Вызывается после того, как OuterScript применил стили
   */
  confirmOpen = (): void => {
    runInAction(() => {
      this._isOpen = true;
    });
  };

  close = (): void => {
    if (!this._isOpen) {
      return;
    }

    this._isOpen = false;

    this._closeAnimationTimerId = setTimeout(() => {
      this._closeAnimationTimerId = null;
      this._sendMessageToParent(WidgetEvents.CLOSED);
    }, WIDGET_ANIMATION_DURATION_MS);
  };

  toggleOpen = async (): Promise<void> => {
    if (this._isOpen) {
      this.close();
    } else {
      await this.open();
    }
  };

  hideUntilReload = (): void => {
    if (this._closeAnimationTimerId !== null) {
      clearTimeout(this._closeAnimationTimerId);
      this._closeAnimationTimerId = null;
    }

    this._isOpen = false;
    this._sendMessageToParent(WidgetEvents.HIDE_UNTIL_RELOAD_REQUEST);
  };

  handleMediaViewerOpened = (): void => {
    document.body.style.overflow = 'hidden';

    if (this._layoutVersion === LayoutVersion.DESKTOP) {
      const widgetEl = document.getElementById(WIDGET_ID);
      if (widgetEl) {
        widgetEl.style.opacity = '0';
      }
    }

    this._sendMessageToParent(WidgetEvents.MEDIA_VIEWER_OPENED);
  };

  handleMediaViewerClosed = (): void => {
    const widgetEl = document.getElementById(WIDGET_ID);

    document.body.style.overflow = '';
    if (widgetEl) {
      widgetEl.style.opacity = '';

      this._sendMessageToParent(WidgetEvents.MEDIA_VIEWER_CLOSED);
    }
  };

  destroy = (): void => {
    if (this._closeAnimationTimerId !== null) {
      clearTimeout(this._closeAnimationTimerId);
      this._closeAnimationTimerId = null;
    }
    this._isOpen = false;
    this.webSocketStore.disconnect();
    this.messagesStore.destroy();
  };

  private _sendMessageToParent(
    event: WidgetEvents,
    payload?: TokenPayload
  ): void {
    if (!window.parent) {
      return;
    }

    const message = {
      source: MessageSource.WIDGET,
      event,
      payload
    };

    window.parent.postMessage(message, '*');
  }
}

export const rootStore = new RootStore();
